import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

const buildDays = () => Array.from({ length: 31 }, (_, index) => String(index + 1));

const QuestionStep = ({
  question,
  answer,
  affirmation,
  errorMessage,
  onBack,
  onNext,
  onSingleSelect,
  onMultiToggle,
  onOtherTextChange,
  onDateChange
}) => {
  const nextButtonRef = useRef(null);
  const value = answer?.value;
  const otherText = answer?.otherText || '';
  const isMulti = question.type === 'multi';
  const isSingle = question.type === 'single';
  const isDate = question.type === 'date';
  const selectedValues = Array.isArray(value) ? value : value ? [value] : [];
  const exclusiveSelected = Boolean(
    question.exclusiveOptions?.length && selectedValues.some(option => question.exclusiveOptions.includes(option))
  );
  const maxSelectionsReached = Boolean(question.maxSelections && selectedValues.length >= question.maxSelections);

  const shouldShowOther = question.otherOption
    ? selectedValues.includes(question.otherOption.value) || value === question.otherOption.value
    : false;

  // Determine if the question has any valid answer
  const hasAnswer = () => {
    if (isDate) {
      return !!(answer?.date?.month || answer?.date?.day || answer?.date?.year);
    }
    if (isMulti) {
      return selectedValues.length > 0;
    }
    if (isSingle) {
      return !!value;
    }
    return false;
  };

  // Blur the next button when the question changes
  useEffect(() => {
    if (nextButtonRef.current) {
      nextButtonRef.current.blur();
    }
  }, [question.id]);

  // Determine button text based on whether question is optional and has an answer
  const getButtonText = () => {
    if (question.nextText) {
      return question.nextText;
    }
    if (!question.required && !hasAnswer()) {
      return 'Skip Question';
    }
    return 'Next Question';
  };

  const handleOptionChange = optionValue => {
    if (isSingle) {
      onSingleSelect(question.id, optionValue);
      return;
    }
    onMultiToggle(question, optionValue);
  };

  const renderOption = option => {
    const isChecked = selectedValues.includes(option.value);
    const isExclusiveOption = question.exclusiveOptions?.includes(option.value);
    const isDisabled =
      isMulti &&
      !isChecked &&
      ((exclusiveSelected && !isExclusiveOption) || (maxSelectionsReached && !isExclusiveOption));

    return (
      <label
        key={option.value}
        className={`intake-option ${isDisabled ? 'is-disabled' : ''} ${isChecked ? 'is-selected' : ''}`}
      >
        <input
          type={isSingle ? 'radio' : 'checkbox'}
          name={question.id}
          value={option.value}
          checked={isChecked}
          disabled={isDisabled}
          onChange={() => handleOptionChange(option.value)}
        />
        <span>{option.label}</span>
      </label>
    );
  };

  const renderAffirmation = text => {
    if (!text) return null;
    const match = text.match(/[.!?;:,]/);
    if (!match) return text;

    const punctuationIndex = match.index;
    const boldPart = text.substring(0, punctuationIndex + 1);
    const normalPart = text.substring(punctuationIndex + 1);

    return (
      <>
        <strong>{boldPart}</strong>
        {normalPart}
      </>
    );
  };

  const renderOptions = options => <div className="intake-options">{options.map(renderOption)}</div>;

  return (
    <div className="intake-step">
      <h2 className="intake-title">{question.title}</h2>
      {question.description && <p className="intake-description">{question.description}</p>}

      {isDate && (
        <div className="intake-date-grid">
          <label className="intake-field">
            <span>Month</span>
            <select
              value={answer?.date?.month || ''}
              onChange={event => onDateChange(question.id, 'month', event.target.value)}
            >
              <option value="">Select month</option>
              {MONTHS.map(month => (
                <option key={month} value={month}>
                  {month}
                </option>
              ))}
            </select>
          </label>
          <label className="intake-field">
            <span>Day</span>
            <select
              value={answer?.date?.day || ''}
              onChange={event => onDateChange(question.id, 'day', event.target.value)}
            >
              <option value="">Select day</option>
              {buildDays().map(day => (
                <option key={day} value={day}>
                  {day}
                </option>
              ))}
            </select>
          </label>
          <label className="intake-field">
            <span>Year (optional)</span>
            <input
              type="number"
              min="1900"
              max={new Date().getFullYear()}
              value={answer?.date?.year || ''}
              onChange={event => onDateChange(question.id, 'year', event.target.value)}
              placeholder="YYYY"
            />
          </label>
        </div>
      )}

      {question.sections?.length
        ? question.sections.map(section => (
            <div key={section.title} className="intake-section">
              <h4 className="intake-section-title">{section.title}</h4>
              {renderOptions(section.options)}
            </div>
          ))
        : question.options && renderOptions(question.options)}

      {shouldShowOther && question.otherOption && (
        <div className="intake-other">
          {question.otherOption.prompt && <p className="intake-other-prompt">{question.otherOption.prompt}</p>}
          <input
            type="text"
            className="intake-other-input"
            value={otherText}
            maxLength={question.otherOption.maxLength}
            placeholder={question.otherOption.placeholder}
            onChange={event => onOtherTextChange(question.id, event.target.value)}
          />
          {question.otherOption.helperText && <p className="intake-helper-text">{question.otherOption.helperText}</p>}
        </div>
      )}

      {errorMessage && <div className="intake-error">* {errorMessage}</div>}
      {affirmation && <div className="alert alert-purple">{renderAffirmation(affirmation)}</div>}

      <div className="intake-actions">
        {onBack && (
          <button type="button" className="btn btn-light px-4" onClick={onBack}>
            Back
          </button>
        )}
        <button
          ref={nextButtonRef}
          type="button"
          className="btn btn-primary px-4"
          onClick={onNext}
          disabled={!!errorMessage}
        >
          {getButtonText()}
        </button>
      </div>
    </div>
  );
};

QuestionStep.propTypes = {
  question: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    type: PropTypes.string.isRequired,
    options: PropTypes.array,
    sections: PropTypes.array,
    nextText: PropTypes.string,
    exclusiveOptions: PropTypes.array,
    otherOption: PropTypes.shape({
      value: PropTypes.string.isRequired,
      prompt: PropTypes.string,
      helperText: PropTypes.string,
      placeholder: PropTypes.string,
      maxLength: PropTypes.number
    })
  }).isRequired,
  answer: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    otherText: PropTypes.string,
    date: PropTypes.shape({
      month: PropTypes.string,
      day: PropTypes.string,
      year: PropTypes.string
    })
  }),
  affirmation: PropTypes.string,
  errorMessage: PropTypes.string,
  onBack: PropTypes.func,
  onNext: PropTypes.func.isRequired,
  onSingleSelect: PropTypes.func.isRequired,
  onMultiToggle: PropTypes.func.isRequired,
  onOtherTextChange: PropTypes.func.isRequired,
  onDateChange: PropTypes.func.isRequired
};

export default QuestionStep;
